import { useEffect, useState } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { Save, AlertCircle } from 'lucide-react'

interface Thresholds {
  sexual: number
  'sexual/minors': number
  violence: number
  harassment: number
  hate: number
}

interface ModerationSettings {
  isActive: boolean
  thresholds: Thresholds
}

const DEFAULT_THRESHOLDS: Thresholds = {
  sexual: 0.3,
  'sexual/minors': 0.05,
  violence: 0.2,
  harassment: 0.15,
  hate: 0.2,
}

// Helper function to get severity label for current threshold
function getSeverityLabel(value: number): string {
  if (value <= 0.1) return 'Çok Katı'
  if (value <= 0.15) return 'Katı'
  if (value <= 0.2) return 'Orta-Katı'
  if (value <= 0.25) return 'Orta'
  if (value <= 0.3) return 'Orta-Esnek'
  if (value <= 0.35) return 'Esnek'
  return 'Çok Esnek'
}

// Helper function to get severity color
function getSeverityColor(value: number): string {
  if (value <= 0.15) return 'text-red-600'
  if (value <= 0.25) return 'text-orange-600'
  if (value <= 0.35) return 'text-yellow-600'
  return 'text-green-600'
}

// Helper function to get severity icon
function getSeverityIcon(value: number): string {
  if (value <= 0.15) return '🔒'
  if (value <= 0.25) return '⚠️'
  if (value <= 0.35) return '⚖️'
  return '✓'
}

export default function ModerationSettingsTab() {
  const [settings, setSettings] = useState<ModerationSettings>({
    isActive: false,
    thresholds: DEFAULT_THRESHOLDS,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      const docRef = doc(db, 'moderationSettings', 'customThresholds')
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data() as ModerationSettings
        setSettings({
          isActive: data.isActive || false,
          thresholds: {
            ...DEFAULT_THRESHOLDS,
            ...data.thresholds,
          },
        })
      } else {
        // Create default document
        await setDoc(docRef, {
          isActive: false,
          thresholds: DEFAULT_THRESHOLDS,
        })
      }
    } catch (error) {
      console.error('Error loading settings:', error)
      setMessage({ type: 'error', text: 'Ayarlar yüklenirken hata oluştu' })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage(null)

    try {
      const docRef = doc(db, 'moderationSettings', 'customThresholds')
      await setDoc(docRef, settings, { merge: true })
      setMessage({ type: 'success', text: 'Ayarlar başarıyla kaydedildi' })
    } catch (error) {
      console.error('Error saving settings:', error)
      setMessage({ type: 'error', text: 'Ayarlar kaydedilirken hata oluştu' })
    } finally {
      setSaving(false)
    }
  }

  const handleThresholdChange = (category: keyof Thresholds, value: number) => {
    setSettings({
      ...settings,
      thresholds: {
        ...settings.thresholds,
        [category]: value,
      },
    })
  }

  const handleToggleActive = () => {
    setSettings({
      ...settings,
      isActive: !settings.isActive,
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  const categoryLabels: Record<keyof Thresholds, string> = {
    sexual: 'Cinsel İçerik',
    'sexual/minors': 'Cinsel İçerik (Minörler)',
    violence: 'Şiddet',
    harassment: 'Taciz',
    hate: 'Nefret Söylemi',
  }

  return (
    <div className="space-y-6">
      {message && (
        <div
          className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          <AlertCircle className="w-5 h-5" />
          <span>{message.text}</span>
        </div>
      )}

      {/* Active Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Özel Threshold'ları Aktif Et</h3>
            <p className="text-sm text-gray-500 mt-1">
              Aktif edildiğinde, OpenAI'in threshold'ları yerine bu değerler kullanılacak
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.isActive}
              onChange={handleToggleActive}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>
      </div>

      {/* Thresholds */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Threshold Değerleri</h3>
        <p className="text-sm text-gray-500 mb-6">
          Her kategori için bir threshold seçin. <strong>Düşük değer = daha katı moderation</strong>. 
          Seçilen değerin üzerindeki AI skorları reddedilecek.
        </p>

        <div className="space-y-6">
          {(Object.keys(settings.thresholds) as Array<keyof Thresholds>).map((category) => {
            const currentValue = settings.thresholds[category]
            const severityLabel = getSeverityLabel(currentValue)
            const percentage = Math.round(currentValue * 100)
            const severityColor = getSeverityColor(currentValue)
            const severityIcon = getSeverityIcon(currentValue)
            
            return (
              <div key={category} className="border border-gray-200 rounded-lg p-5 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-gray-900 mb-1">
                      {categoryLabels[category]}
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-sm font-medium ${severityColor}`}>
                        {severityIcon} {severityLabel}
                      </span>
                      <span className="text-sm text-gray-500">
                        (%{percentage})
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">%{percentage}</div>
                    <div className="text-xs text-gray-500">Threshold</div>
                  </div>
                </div>
                
                <div className="relative">
                  <style>{`
                    input[type="range"] {
                      -webkit-appearance: none;
                      appearance: none;
                      background: transparent;
                      cursor: pointer;
                    }
                    
                    input[type="range"]::-webkit-slider-track {
                      background: linear-gradient(to right, #ef4444 0%, #f97316 25%, #eab308 50%, #22c55e 100%);
                      height: 8px;
                      border-radius: 4px;
                    }
                    
                    input[type="range"]::-moz-range-track {
                      background: linear-gradient(to right, #ef4444 0%, #f97316 25%, #eab308 50%, #22c55e 100%);
                      height: 8px;
                      border-radius: 4px;
                    }
                    
                    input[type="range"]::-webkit-slider-thumb {
                      -webkit-appearance: none;
                      appearance: none;
                      background: white;
                      border: 2px solid hsl(346.8, 77.2%, 49.8%);
                      height: 20px;
                      width: 20px;
                      border-radius: 50%;
                      cursor: pointer;
                      margin-top: -6px;
                      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                    }
                    
                    input[type="range"]::-moz-range-thumb {
                      background: white;
                      border: 2px solid hsl(346.8, 77.2%, 49.8%);
                      height: 20px;
                      width: 20px;
                      border-radius: 50%;
                      cursor: pointer;
                      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                    }
                    
                    input[type="range"]:disabled::-webkit-slider-thumb {
                      cursor: not-allowed;
                      opacity: 0.5;
                    }
                    
                    input[type="range"]:disabled::-moz-range-thumb {
                      cursor: not-allowed;
                      opacity: 0.5;
                    }
                  `}</style>
                  <input
                    type="range"
                    min="0.05"
                    max="0.7"
                    step="0.01"
                    value={currentValue}
                    onChange={(e) => handleThresholdChange(category, parseFloat(e.target.value))}
                    disabled={!settings.isActive}
                    className="w-full"
                  />
                  
                  {/* Slider markers */}
                  <div className="flex justify-between mt-1 text-xs text-gray-400">
                    <span>5%</span>
                    <span>20%</span>
                    <span>35%</span>
                    <span>50%</span>
                    <span>70%</span>
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mt-3">
                  <strong>Düşük değer = Daha katı:</strong> %{percentage} seçildiğinde, AI'ın bu kategori için 
                  %{percentage} veya üzerinde skor verdiği içerikler reddedilir.
                </p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>
    </div>
  )
}
